/* eslint-disable react/no-unstable-nested-components */
// v3.15-926: Calendar page — Day / Week / Month views. Shows all appointments
// assigned to the current user (or company-wide for admins). Status-colour-
// coded; click any block to open the details modal and reschedule / change
// status / edit notes.
// v3.15-927: Phase 3 — Copy Booking Link header button + "new appointment"
// badge on tiles the current user hasn't viewed yet.
// v3.15-1081: Tasks now appear on the Calendar alongside appointments. A
// filter toggle lets the user choose Appointments / Tasks / Both. Tasks are
// rendered with a clipboard icon prefix and a distinct purple accent so they
// remain visually separated from appointments. Multi-assignee tasks appear on
// every assignee's calendar (backend authoritative).
// v3.18-1345 A7 — Header now exposes a "+ New Appointment" button that opens
// a client picker → ScheduleAppointmentModal so an operator starting from
// the calendar can create an appointment without navigating away.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2, Copy, CheckCircle, Settings, Sparkles, Clipboard, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import AppointmentDetailsModal from '../components/AppointmentDetailsModal';
import TaskQuickViewModal from '../components/TaskQuickViewModal';
import ScheduleAppointmentModal from '../components/ScheduleAppointmentModal';
import { ClientSelector } from '../components/ClientSelector';

const STATUS_BG = {
  scheduled:       'bg-blue-100  text-blue-800   border-blue-300',
  confirmed:       'bg-green-100 text-green-800  border-green-300',
  completed:       'bg-slate-200 text-slate-700  border-slate-300',
  no_show:         'bg-red-100   text-red-800    border-red-300',
  rescheduled:     'bg-amber-100 text-amber-800  border-amber-300',
  cancelled:       'bg-slate-100 text-slate-500  border-slate-200',
  pending_outcome: 'bg-red-50    text-red-700    border-red-400 ring-1 ring-red-400',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// v3.15-928: friendly labels for common IANA zones used in the hub.
const TZ_LABELS = {
  'America/New_York':    'Eastern Time',
  'America/Chicago':     'Central Time',
  'America/Denver':      'Mountain Time',
  'America/Phoenix':     'Arizona Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage':   'Alaska Time',
  'Pacific/Honolulu':    'Hawaii Time',
  'UTC':                 'UTC',
};

// ---- date helpers ----
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; };
const startOfMonth = (d) => { const x = startOfDay(d); x.setDate(1); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const iso = (d) => d.toISOString();
const sameDay = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView]   = useState('week');    // day | week | month
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  // v3.15-1081: a task is selected separately because it opens a different
  // modal than the appointment-details modal.
  const [selectedTask, setSelectedTask] = useState(null);
  const [bookingSlug, setBookingSlug] = useState('');
  const [repTz, setRepTz] = useState('America/New_York');  // v3.15-928
  const [copied, setCopied] = useState(false);
  const [viewedIds, setViewedIds] = useState(() => new Set());
  // v3.15-1081: filter — 'both' | 'appointments' | 'tasks'
  const [filter, setFilter] = useState('both');

  // v3.18-1345 A7 — new-appointment flow state. clientPickerOpen controls
  // the client-lookup step; scheduleClient is passed to the existing
  // ScheduleAppointmentModal once a client is picked.
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState(null);
  const [scheduleClient, setScheduleClient] = useState(null);

  // v3.15-928: render times in the rep's configured timezone.
  const fmtTime = useCallback((isoStr) => {
    try {
      return new Date(isoStr).toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true, timeZone: repTz,
      });
    } catch {
      return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }, [repTz]);

  const repTzLabel = TZ_LABELS[repTz] || repTz;

  // Fetch rep settings once — gives us the booking slug for the header button.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/appointments/me/settings');
        setBookingSlug(data?.settings?.booking_slug || '');
        if (data?.settings?.timezone) setRepTz(data.settings.timezone);
      } catch { /* no-op */ }
      try {
        const { data } = await api.get('/appointments/me/viewed');
        setViewedIds(new Set(data?.viewed_ids || []));
      } catch { /* no-op */ }
    })();
  }, []);

  const publicBookingUrl = useMemo(
    () => (bookingSlug ? `${window.location.origin}/book/${bookingSlug}` : ''),
    [bookingSlug],
  );

  const copyBookingLink = async () => {
    if (!publicBookingUrl) {
      toast.error('Set your booking slug in Settings → Availability');
      return;
    }
    try {
      await navigator.clipboard.writeText(publicBookingUrl);
      setCopied(true);
      toast.success('Booking link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Compute range bounds for the fetch — always a bit wider than the view so
  // edge-case events near boundaries render correctly.
  const { rangeStart, rangeEnd, title } = useMemo(() => {
    if (view === 'day') {
      const s = startOfDay(cursor);
      return { rangeStart: s, rangeEnd: addDays(s, 1),
               title: cursor.toLocaleDateString('en-US', { weekday:'long', month:'short', day:'numeric', year:'numeric' }) };
    }
    if (view === 'week') {
      const s = startOfWeek(cursor);
      return { rangeStart: s, rangeEnd: addDays(s, 7),
               title: `${s.toLocaleDateString('en-US',{month:'short', day:'numeric'})} – ${addDays(s,6).toLocaleDateString('en-US',{month:'short', day:'numeric', year:'numeric'})}` };
    }
    // month
    const s = startOfMonth(cursor);
    const gridStart = startOfWeek(s);
    const gridEnd = addDays(gridStart, 42);
    return { rangeStart: gridStart, rangeEnd: gridEnd,
             title: cursor.toLocaleDateString('en-US',{month:'long', year:'numeric'}) };
  }, [view, cursor]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      // v3.15-1081: parallel-fetch both feeds based on the active filter.
      const wantApts  = filter === 'both' || filter === 'appointments';
      const wantTasks = filter === 'both' || filter === 'tasks';
      const params = { from_date: iso(rangeStart), to_date: iso(rangeEnd), limit: 500 };
      const [aptRes, taskRes] = await Promise.all([
        wantApts  ? api.get('/appointments',    { params }).catch(() => null) : null,
        wantTasks ? api.get('/tasks/calendar',  { params }).catch(() => null) : null,
      ]);
      const appts = (aptRes?.data?.items || []).map((a) => ({ ...a, item_type: a.item_type || 'appointment' }));
      const tasks = taskRes?.data?.items || [];
      setItems([...appts, ...tasks]);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [rangeStart, rangeEnd, filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const shift = (delta) => {
    if (view === 'day')   setCursor((d) => addDays(d, delta));
    if (view === 'week')  setCursor((d) => addDays(d, delta * 7));
    if (view === 'month') setCursor((d) => addMonths(d, delta));
  };

  const onUpdated = (updated) => {
    setItems((prev) => prev.map((i) => i.id === updated.id ? { ...i, ...updated } : i));
    fetchItems();
  };

  // v3.15-1081: applied to both appointment + task updates from their modals.
  const onTaskUpdated = (updated) => {
    setItems((prev) => prev.map((i) =>
      i.item_type === 'task' && i.task_id === updated.task_id
        ? { ...i, ...updated }
        : i
    ));
    fetchItems();
  };

  const openAppointment = (a) => {
    setSelected(a);
    // Mark this appointment as viewed (server-side) if we haven't already.
    if (a?.id && !viewedIds.has(a.id)) {
      const next = new Set(viewedIds);
      next.add(a.id);
      setViewedIds(next);
      api.post('/appointments/me/viewed', { appointment_ids: [a.id] }).catch(() => {});
    }
  };

  // v3.15-1081: clicking a task tile opens the task quick-view modal.
  const openItem = (a) => {
    if (a?.item_type === 'task') {
      setSelectedTask(a);
    } else {
      openAppointment(a);
    }
  };

  // Cell rendering ------------------------------------------------------------
  const renderItem = (a, compact = false) => {
    // v3.15-1081: branch on item_type so tasks render with a distinct style.
    if (a?.item_type === 'task') {
      const isCompleted = a.status === 'completed';
      return (
        <button
          key={a.id}
          type="button"
          onClick={() => openItem(a)}
          className={`text-left w-full text-[11px] leading-tight px-1.5 py-1 rounded border hover:brightness-95 transition flex items-start gap-1
            ${isCompleted
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 line-through opacity-80'
              : 'bg-purple-100 text-purple-800 border-purple-300'}`}
          data-testid={`calendar-task-${a.task_id}`}
          data-item-type="task"
        >
          <Clipboard className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-70" />
          <span className="flex-1 truncate">
            <span className="font-semibold">
              {a.due_time ? `${fmtTime(a.date_time_start)} • ` : ''}
              {a.title}
            </span>
            {!compact && a.client_name && (
              <span className="block text-[10px] opacity-75 truncate">{a.client_name}</span>
            )}
          </span>
        </button>
      );
    }
    const isNew = a?.id && !viewedIds.has(a.id);
    return (
      <button
        key={a.id}
        type="button"
        onClick={() => openItem(a)}
        className={`text-left w-full text-[11px] leading-tight px-1.5 py-1 rounded border relative ${STATUS_BG[a.status] || STATUS_BG.scheduled} hover:brightness-95 transition ${isNew ? 'ring-2 ring-indigo-400 ring-offset-1' : ''}`}
        data-testid={`calendar-item-${a.id}`}
        data-new={isNew ? 'true' : 'false'}
        data-item-type="appointment"
      >
        {isNew && (
          <span
            className="absolute -top-1 -right-1 bg-indigo-600 text-white text-[8px] font-bold uppercase tracking-wide px-1 py-0.5 rounded shadow flex items-center gap-0.5"
            data-testid={`calendar-new-badge-${a.id}`}
          >
            <Sparkles className="h-2 w-2" /> NEW
          </span>
        )}
        <div className="font-semibold truncate">
          {fmtTime(a.date_time_start)} {!compact && a.client_name ? `• ${a.client_name}` : a.client_name}
        </div>
        {!compact && a.status && (
          <div className="text-[10px] opacity-75 truncate">{a.status.replace('_', ' ')}</div>
        )}
      </button>
    );
  };

  // ---- view bodies ----
  const DayView = () => {
    const today = startOfDay(cursor);
    const itemsToday = items
      .filter((a) => sameDay(new Date(a.date_time_start), today))
      .sort((a,b) => new Date(a.date_time_start) - new Date(b.date_time_start));
    return (
      <div className="space-y-2" data-testid="calendar-day-view">
        {itemsToday.length === 0 ? (
          <div className="text-center text-slate-400 py-12">
            {filter === 'tasks' ? 'No tasks today.' : filter === 'appointments' ? 'No appointments today.' : 'Nothing on the calendar today.'}
          </div>
        ) : (
          itemsToday.map((a) => (
            <div key={a.id} className="px-2">
              {renderItem(a)}
            </div>
          ))
        )}
      </div>
    );
  };

  const WeekView = () => {
    const start = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return (
      <div className="grid grid-cols-7 gap-2" data-testid="calendar-week-view">
        {days.map((d) => {
          const dayItems = items
            .filter((a) => sameDay(new Date(a.date_time_start), d))
            .sort((a,b) => new Date(a.date_time_start) - new Date(b.date_time_start));
          const isToday = sameDay(d, new Date());
          return (
            <div key={d.toISOString()} className={`min-h-[180px] border rounded-lg p-1.5 space-y-1 ${isToday ? 'border-blue-400 bg-blue-50/30' : 'border-slate-200 bg-white'}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex items-center justify-between px-0.5">
                <span>{DAY_NAMES[d.getDay()]}</span>
                <span className={isToday ? 'text-blue-600' : ''}>{d.getDate()}</span>
              </div>
              {dayItems.map((a) => renderItem(a, true))}
            </div>
          );
        })}
      </div>
    );
  };

  const MonthView = () => {
    const start = startOfWeek(startOfMonth(cursor));
    const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    const monthIndex = cursor.getMonth();
    return (
      <div data-testid="calendar-month-view">
        <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-t">
          {DAY_NAMES.map((d) => (
            <div key={d} className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-2 py-1.5 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 border-t-0 rounded-b">
          {cells.map((d) => {
            const isCurrentMonth = d.getMonth() === monthIndex;
            const dayItems = items.filter((a) => sameDay(new Date(a.date_time_start), d));
            const isToday = sameDay(d, new Date());
            return (
              <div key={d.toISOString()} className={`min-h-[90px] p-1 ${isCurrentMonth ? 'bg-white' : 'bg-slate-50'} ${isToday ? 'ring-2 ring-blue-400' : ''}`}>
                <div className={`text-[10px] font-semibold mb-1 ${isCurrentMonth ? 'text-slate-700' : 'text-slate-400'}`}>{d.getDate()}</div>
                <div className="space-y-0.5">
                  {dayItems.slice(0, 3).map((a) => renderItem(a, true))}
                  {dayItems.length > 3 && (
                    <div className="text-[10px] text-slate-500 pl-1">+{dayItems.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="calendar-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <CalendarIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Calendar</h1>
            <p className="text-xs text-slate-500">
              Internal appointments — Day / Week / Month
              <span className="mx-1.5 text-slate-300">·</span>
              <span data-testid="calendar-timezone-label" className="text-slate-500">Times in {repTzLabel}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* v3.18-1345 A7 — Create new appointment from the calendar. */}
          <Button
            size="sm"
            onClick={() => { setPickerValue(null); setClientPickerOpen(true); }}
            data-testid="calendar-new-appointment-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New Appointment
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={copyBookingLink}
            disabled={!publicBookingUrl}
            data-testid="calendar-copy-booking-link"
            title={publicBookingUrl || 'Configure your booking slug in Settings → Availability'}
          >
            {copied ? <CheckCircle className="h-3.5 w-3.5 mr-1 text-green-600" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
            {copied ? 'Copied' : 'Copy Booking Link'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings/availability')}
            data-testid="calendar-availability-settings"
            title="Rep Availability Settings"
          >
            <Settings className="h-3.5 w-3.5 mr-1" /> Availability
          </Button>
          <div className="flex border border-slate-200 rounded-lg overflow-hidden" data-testid="calendar-view-toggle">
            {['day', 'week', 'month'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-medium transition ${view === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid={`calendar-view-${v}`}
              >
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today-btn">Today</Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-slate-100" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
          <div className="text-sm font-semibold text-slate-800 min-w-[220px] text-center" data-testid="calendar-range-title">{title}</div>
          <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-slate-100" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        {/* v3.15-1081: filter pills — Appointments / Tasks / Both */}
        <div className="flex items-center gap-3">
          <div
            className="flex border border-slate-200 rounded-lg overflow-hidden"
            data-testid="calendar-filter-toggle"
          >
            {[
              { v: 'both',         label: 'Appts + Tasks' },
              { v: 'appointments', label: 'Appointments' },
              { v: 'tasks',        label: 'Tasks' },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setFilter(opt.v)}
                className={`px-3 py-1.5 text-xs font-medium transition ${filter === opt.v ? 'bg-purple-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid={`calendar-filter-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {loading && <div className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
        {view === 'day' && <DayView />}
        {view === 'week' && <WeekView />}
        {view === 'month' && <MonthView />}
      </div>

      <AppointmentDetailsModal
        open={!!selected}
        onOpenChange={(o) => { if (!o) setSelected(null); }}
        appointment={selected}
        onUpdated={onUpdated}
      />

      {/* v3.15-1081: task quick-view modal — opens when a task tile is clicked */}
      <TaskQuickViewModal
        open={!!selectedTask}
        onOpenChange={(o) => { if (!o) setSelectedTask(null); }}
        task={selectedTask}
        onUpdated={onTaskUpdated}
      />

      {/* v3.18-1345 A7 — Client picker step. ClientSelector is reusable
          and honours the same /clients search endpoint used everywhere. */}
      <Dialog open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
        <DialogContent className="max-w-md" data-testid="calendar-client-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">New Appointment</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Search for a client to schedule with.</p>
          </DialogHeader>
          <div className="py-2">
            <ClientSelector
              value={pickerValue}
              onChange={setPickerValue}
              placeholder="Search clients by name…"
              testIdPrefix="calendar-new-appt-client"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClientPickerOpen(false); setPickerValue(null); }}>
              Cancel
            </Button>
            <Button
              disabled={!pickerValue?.id}
              onClick={() => {
                setScheduleClient(pickerValue);
                setClientPickerOpen(false);
              }}
              data-testid="calendar-new-appt-continue"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.18-1345 A7 — Existing ScheduleAppointmentModal honours all
          rep-availability rules. currentUser powers the rep resolution. */}
      <ScheduleAppointmentModal
        open={!!scheduleClient}
        onOpenChange={(o) => { if (!o) { setScheduleClient(null); setPickerValue(null); } }}
        client={scheduleClient}
        currentUser={user}
        onScheduled={() => {
          setScheduleClient(null);
          setPickerValue(null);
          // Force a refresh of items so the newly-created appointment appears.
          setCursor(new Date(cursor));
        }}
      />
    </div>
  );
}
